import { inject, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { AppVersionCheckService } from './app-version-check.service';
import { CHUNK_LOAD_ERROR_IGNORE_PATTERNS } from './chunk-load-error-ignore-patterns';

export { CHUNK_LOAD_ERROR_IGNORE_PATTERNS };

/**
 * Service zum Behandeln von ChunkLoadErrors, die auftreten wenn eine neue Version
 * deployed wurde während der User die App verwendet.
 *
 * Strategie:
 * - Bei ChunkLoadError: Alert anzeigen
 */
@Injectable({
  providedIn: 'root',
})
export class ChunkErrorHandlerService {
  private readonly versionCheckService = inject(AppVersionCheckService);

  constructor() {
    this.setupErrorHandler();
    this.setupSentryChunkErrorFilter();
  }

  /**
   * Wird aufgerufen nachdem die App erfolgreich geladen wurde.
   */
  public startVersionCheck(appName: string): void {
    this.versionCheckService.startVersionCheck(appName);
  }

  private setupErrorHandler(): void {
    // Globaler Error Handler für dynamische Imports
    window.addEventListener('error', (event: ErrorEvent) => {
      const error = event.error;
      if (this.isChunkLoadError(error) || this.isDynamicImportError(event)) {
        event.preventDefault();
        this.handleChunkError();
      }
    });

    // Handler für unhandled promise rejections (z.B. bei lazy loading)
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const error = event.reason;
      if (this.isChunkLoadError(error) || this.isDynamicImportError(error)) {
        event.preventDefault();
        this.handleChunkError();
      }
    });
  }

  /**
   * Drops chunk load events caused by an outdated tab or a missing connection and keeps all others, tagged for filtering.
   */
  private setupSentryChunkErrorFilter(): void {
    Sentry.addEventProcessor(async (event, hint) => {
      const originalException = hint.originalException;
      const eventMessage = event.exception?.values?.[0]?.value ?? '';
      const isChunkEvent = CHUNK_LOAD_ERROR_IGNORE_PATTERNS.some(
        (pattern) => pattern.test(eventMessage) || (originalException instanceof Error && pattern.test(originalException.message)),
      );
      if (!isChunkEvent) return event;

      if (!navigator.onLine) return null;

      const versionStatus = await this.versionCheckService.getVersionStatus();
      if (versionStatus === 'new-version' || versionStatus === 'unreachable') return null;

      event.tags = { ...event.tags, chunk_error: versionStatus };
      return event;
    });
  }

  private isChunkLoadError(error: unknown): boolean {
    if (!error) return false;

    const errorMessage = error instanceof Error ? error.message : String(error);

    // Verschiedene Chunk-Load-Error Patterns
    return (
      errorMessage.includes('ChunkLoadError') ||
      errorMessage.includes('Loading chunk') ||
      errorMessage.includes('Failed to fetch dynamically imported module') ||
      errorMessage.includes('chunk-') ||
      (error instanceof Error && error.name === 'ChunkLoadError')
    );
  }

  private isDynamicImportError(error: unknown): boolean {
    if (!error) return false;

    const errorMessage = error instanceof Error ? error.message : String(error);

    return errorMessage.includes('Failed to fetch') && errorMessage.includes('.js');
  }

  private handleChunkError(): void {
    this.versionCheckService.showAlert();
  }
}
